import React from 'react';
import { Paper, Typography, List } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FormField } from '../../types/form';
import { SortableFieldItem } from './SortableFieldItem';

// ============================================================================
// Interfaces
// ============================================================================
interface AvailableFieldsProps {
  fields: FormField[];
}

// ============================================================================
// Component
// ============================================================================
export const AvailableFields = ({ fields }: AvailableFieldsProps) => {
  const { setNodeRef, isOver, active } = useDroppable({
    id: 'available-fields'
  });

  const isValidDrop = active?.id?.toString().startsWith('field-');

  return (
    <Paper 
      ref={setNodeRef}
      sx={{ 
        width: 250,
        p: 2,
        backgroundColor: isOver && isValidDrop ? 'action.selected' : 'background.paper',
        transition: 'all 0.2s ease',
        borderColor: isOver && isValidDrop ? 'primary.main' : 'divider',
        borderWidth: isOver && isValidDrop ? 2 : 1,
        borderStyle: 'solid',
        boxShadow: isOver && isValidDrop ? 4 : 1,
        position: 'relative',
        '&::after': isOver && isValidDrop ? {
          content: '"Drop to remove from form"',
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'primary.main',
          fontSize: '0.75rem',
          fontWeight: 'bold'
        } : {}
      }}
    >
      <Typography variant="h6" gutterBottom>
        Available Fields
      </Typography>
      <SortableContext 
        items={fields.map(f => `available-${f.field_name}`)}
        strategy={verticalListSortingStrategy}
      >
        <List sx={{ bgcolor: 'background.paper', minHeight: 100 }}>
          {fields.map((field) => (
            <SortableFieldItem
              key={`available-${field.field_name}`}
              id={`available-${field.field_name}`}
              field={{
                id: `field-${field.field_name}`,
                name: field.field_name,
                label: field.label || field.field_name,
                type: field.type || 'text',
                width: 100,
                required: Boolean(field.required),
                sectionId: '',
                sectionTitle: ''
              }}
              onEdit={() => { /* No edit action needed for available fields */ }}
              isAvailable
            />
          ))}
        </List>
      </SortableContext>
    </Paper>
  );
};
